import { FeeSource, feeBreakdown } from './PractitionerFee.Logic';

/**
 * Edición coherente del cálculo del honorario. El honorario nunca se escribe
 * a mano: sale de los demás valores, así no puede quedar incoherente con ellos.
 *
 *   base      = facturado − retención − costo   (mínimo 0)
 *   honorario = base × porcentaje / 100          (mínimo 0, a centavos)
 */

/** Estado NotSettled de ProtocolConsumedServiceStatus: el único que admite cambios. */
export const NOT_SETTLED_STATUS = 0;

export const canEditFee = (status: number | undefined | null): boolean => status === NOT_SETTLED_STATUS;

export interface FeeDraft {
    source: string;
    withholding: string;
    discountedCost: string;
    percentage: string;
}

export interface FeeDraftErrors {
    source?: string;
    withholding?: string;
    discountedCost?: string;
    percentage?: string;
    /** Error de conjunto: las deducciones superan lo facturado. */
    deductions?: string;
}

export interface FeeDerived {
    base: number;
    fee: number;
}

const round2 = (value: number): number => Math.round(value * 100) / 100;

/** Acepta coma o punto decimal; vacío es 0. Devuelve NaN si no es un número. */
export const parseAmount = (text: string): number => {
    const trimmed = (text ?? '').trim();
    if (!trimmed) return 0;
    return Number(trimmed.replace(',', '.'));
};

const numberText = (value: number): string => (Number.isFinite(value) ? String(round2(value)) : '');

export const draftFromSource = (item: FeeSource): FeeDraft => {
    const b = feeBreakdown(item);
    return {
        source: numberText(b.source),
        withholding: numberText(b.withholding),
        discountedCost: numberText(b.discountedCost),
        percentage: numberText(b.percentage)
    };
};

export const validateFeeDraft = (draft: FeeDraft): FeeDraftErrors => {
    const errors: FeeDraftErrors = {};
    const source = parseAmount(draft.source);
    const withholding = parseAmount(draft.withholding);
    const discountedCost = parseAmount(draft.discountedCost);
    const percentage = parseAmount(draft.percentage);

    if (!Number.isFinite(source) || source < 0) errors.source = 'Ingresá un importe mayor o igual a cero.';
    if (!Number.isFinite(withholding) || withholding < 0) errors.withholding = 'Ingresá un importe mayor o igual a cero.';
    if (!Number.isFinite(discountedCost) || discountedCost < 0) errors.discountedCost = 'Ingresá un importe mayor o igual a cero.';
    if (!Number.isFinite(percentage) || percentage < 0 || percentage > 100) errors.percentage = 'El porcentaje va de 0 a 100.';

    if (!errors.source && !errors.withholding && !errors.discountedCost && withholding + discountedCost > source + 0.005) {
        errors.deductions = 'La retención y el costo no pueden superar lo facturado al sponsor.';
    }
    return errors;
};

export const hasFeeErrors = (errors: FeeDraftErrors): boolean => Object.keys(errors).length > 0;

/** Base y honorario derivados del borrador, nunca negativos. Con errores devuelve ceros. */
export const deriveFee = (draft: FeeDraft): FeeDerived => {
    if (hasFeeErrors(validateFeeDraft(draft))) return { base: 0, fee: 0 };
    const base = Math.max(0, round2(parseAmount(draft.source) - parseAmount(draft.withholding) - parseAmount(draft.discountedCost)));
    const fee = Math.max(0, round2(base * parseAmount(draft.percentage) / 100));
    return { base, fee };
};

/** Lo que se guarda: el honorario repartido por unidad, porque la API ajusta el monto unitario. */
export const unitAmountFor = (fee: number, quantity: number | undefined | null): number => {
    const units = quantity && quantity > 0 ? quantity : 1;
    return round2(fee / units);
};

/** El borrador cambió respecto de lo que trae el consumo. */
export const isFeeDraftDirty = (draft: FeeDraft, item: FeeSource): boolean => {
    const original = draftFromSource(item);
    return (Object.keys(original) as (keyof FeeDraft)[]).some((key) => parseAmount(draft[key]) !== parseAmount(original[key]));
};

// ─── Honorario escrito a mano ────────────────────────────────────────────────
// Cuando el honorario no sale de una base de cálculo (valor fijo, lista de
// precios, consumo manual) no hay valores de los que derivarlo: se edita el
// importe total directamente, y se guarda repartido por unidad igual que el otro.

const amountOf = (item: { amount?: number | null }): number =>
    typeof item.amount === 'number' && Number.isFinite(item.amount) ? round2(item.amount) : 0;

export const amountDraftFromSource = (item: { amount?: number | null }): string => numberText(amountOf(item));

export const validateAmountDraft = (text: string): string | undefined => {
    const value = parseAmount(text);
    return !Number.isFinite(value) || value < 0 ? 'Ingresá un importe mayor o igual a cero.' : undefined;
};

export const isAmountDraftDirty = (text: string, item: { amount?: number | null }): boolean =>
    !validateAmountDraft(text) && round2(parseAmount(text)) !== amountOf(item);
